import React, { useEffect, useState } from 'react';
import { useParams, Link,useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { Modal, Button } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faImage, faImages, faSpinner } from '@fortawesome/free-solid-svg-icons';

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

const PendingPost = () => {
    const query = useQuery();
    const ContentId = query.get("ContentId"); // Get ContentId from URL
    const UserID = localStorage.getItem("UserID");
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [updatedContent, setUpdatedContent] = useState('');
    const [selectedPrompt, setSelectedPrompt] = useState('');
    const [promptData, setPromptData] = useState([]);
    const [regenerating, setRegenerating] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [image, setImage] = useState(null);
    const [selectedImagename, setSelectedImagename] = useState('');
    const [fetchedImages, setFetchedImages] = useState([]);
    const [imageKeywords, setImageKeywords] = useState('');
    const [isFetchingImages, setIsFetchingImages] = useState(false);
    const [isRefreshingImages, setIsRefreshingImages] = useState(false); // Loading state for Refresh button
    const [isCraftingVisual, setIsCraftingVisual] = useState(false); // Loading state for Craft Visual button

    useEffect(() => {
        debugger;
        if (!ContentId) return; // Prevent API call if ContentId is missing

        const fetchPosts = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_BASE_URL}/api/getallgeneratedpostsbyContentId/${ContentId}`
                );

                if (response.data.length > 0) {
                    setUpdatedContent(response.data[0]?.Content || "");
                    setSelectedPrompt(response.data[0]?.Prompts || "");
                    setSelectedImagename(response.data[0]?.image || "");
                }
            } catch (err) {
                console.error("Failed to fetch posts:", err.message);
                setError("Failed to fetch posts");
            } finally {
                setLoading(false);
            }
        };

        fetchPosts();
    }, [ContentId]);

    // Extract keywords from content for better image search
    const extractKeywordsFromContent = (content, originalPrompt) => {
        const keywords = [];
        
        // 1. Always include the original prompt (most relevant)
        if (originalPrompt?.trim()) {
            const promptWords = originalPrompt
                .split(/\s+/)
                .filter(word => word.length > 3 && !['the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'her', 'was', 'one', 'our', 'out', 'day', 'get', 'has', 'him', 'his', 'how', 'its', 'may', 'new', 'now', 'old', 'see', 'two', 'way', 'who', 'boy', 'did', 'she', 'use', 'her', 'many', 'than', 'them'].includes(word.toLowerCase()))
                .slice(0, 5);
            keywords.push(...promptWords);
        }
        
        // 2. Extract hashtags from content
        const hashtags = content.match(/#[\w]+/g) || [];
        keywords.push(...hashtags.map(tag => tag.replace('#', '')));
        
        // 3. Extract key nouns/phrases from content
        const capitalizedWords = content.match(/\b[A-Z][a-z]+\b/g) || [];
        keywords.push(...capitalizedWords.slice(0, 3));
        
        // Remove duplicates and limit to 5-7 keywords
        const uniqueKeywords = [...new Set(keywords)].slice(0, 7);
        return uniqueKeywords.filter(k => k && k.length > 2);
    };

    // Function to fetch images with current keywords
    const fetchImagesWithKeywords = async (isRefresh = false) => {
        if (!updatedContent?.trim()) {
            return;
        }
        
        // Set appropriate loading state based on which button was clicked
        if (isRefresh) {
            setIsRefreshingImages(true);
        } else if (imageKeywords?.trim()) {
            setIsCraftingVisual(true);
        } else {
            setIsFetchingImages(true);
        }
        
        try {
            // If user has manually entered keywords, use those
            if (imageKeywords?.trim() && !isRefresh) {
                const manualKeywords = imageKeywords.split(',').map(k => k.trim()).filter(k => k);
                await fetchImagesForTags(manualKeywords);
                setIsCraftingVisual(false);
                return;
            }
            
            // Otherwise, use improved automatic extraction
            const extractedKeywords = extractKeywordsFromContent(updatedContent, selectedPrompt);
            console.log('Extracted keywords for images:', extractedKeywords);
            
            if (extractedKeywords.length > 0) {
                await fetchImagesForTags(extractedKeywords);
            } else {
                // Fallback to hashtags
                const hashtags = updatedContent.match(/#[\w]+/g) || [];
                await fetchImagesForTags(hashtags.slice(-5));
            }
        } catch (error) {
            console.error('Error fetching images:', error);
            setFetchedImages([]);
        } finally {
            setIsFetchingImages(false);
            setIsRefreshingImages(false);
            setIsCraftingVisual(false);
        }
    };

    useEffect(() => {
        if (updatedContent?.trim() && !imageKeywords?.trim()) {
            fetchImagesWithKeywords();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updatedContent, selectedPrompt]);

    const validateImage = async (url) => {
        return new Promise((resolve) => {
            const img = new Image();
            const timeout = setTimeout(() => {
                resolve(null); // Timeout after 3 seconds
            }, 3000);
            
            img.onload = () => {
                clearTimeout(timeout);
                resolve(url);
            };
            img.onerror = () => {
                clearTimeout(timeout);
                resolve(null);
            };
            img.src = url;
        });
    };

    const fetchImagesForTags = async (tags) => {
        if (!tags.length) return;

        try {
            const query = tags.join(" ");
            const response = await axios.get("https://www.googleapis.com/customsearch/v1", {
                params: {
                    key: process.env.REACT_APP_GOOGLE_API_KEY,
                    cx: process.env.REACT_APP_GOOGLE_CSE_ID,
                    q: query,
                    searchType: "image",
                    num: 10,
                    rights: 'cc_publicdomain,cc_attribute',
                    safe: 'active',
                },
            });

            if (response.data.items) {
                // Validate only first 8 images in parallel (faster), with timeout
                const itemsToValidate = response.data.items.slice(0, 8);
                const imageLinks = await Promise.all(
                    itemsToValidate.map((item) => validateImage(item.link))
                );
                const validImages = imageLinks.filter((link) => link !== null);

                if (validImages.length === 0) {
                    setFetchedImages([]); // Ensure state is empty
                } else {
                    setFetchedImages(validImages);
                }
            } else {
                setFetchedImages([]);
            }
        } catch (error) {
            console.error("Error fetching images:", error);
            setFetchedImages([]);
        }
    };


    const handlePromptChange = (e) => {
        setSelectedPrompt(e.target.value);
    };

    const handleRegenerate = async (ContentId, Prompts) => {
        setRegenerating(true);
        setImageKeywords(''); // Clear image keywords when regenerating
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/generateAIContentinUpdatepost`,
                { UserID, Prompts },
                { headers: { 'Content-Type': 'application/json' } }
            );

            const newContent = response.data.content;

            Swal.fire({
                position: 'center',
                icon: 'success',
                title: 'Post regenerated successfully',
                showConfirmButton: false,
                timer: 3000
            });

            setUpdatedContent(newContent);
        } catch (err) {
            console.error('Failed to regenerate post:', err.message);
            setError('Failed to regenerate post');
        } finally {
            setRegenerating(false);
        }
    };

    const [selectedImageUrl, setSelectedImageUrl] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);

    const handleSelectSuggestedImage = (imageUrl) => {
        setSelectedImageUrl(imageUrl);
        setImage(null);
        // setSelectedImagename(imageUrl);
        setIsModalOpen(true);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        const maxSize = 50 * 1024 * 1024; // 50 MB in bytes

        if (file && file.size > maxSize) {
            Swal.fire({
                icon: 'error',
                title: 'File Size Exceeded',
                text: 'The file size should not exceed 50 MB.',
                showConfirmButton: true,
            });
            setImage(null);
            setSelectedImagename('');
        } else {
            setImage(file);
            setSelectedImagename(file.name);
        }
    };


    const handleSaveUpdate = async () => {
        if (!updatedContent) return;

        setLoading(true);
        const formData = new FormData();
        formData.append('ContentId', ContentId);
        formData.append('Content', updatedContent);
        formData.append('Prompts', selectedPrompt);
        formData.append('QuestionwithOptions', null);

        if (image) {
            formData.append('image', image); // Upload directly if it's a file
        } else if (selectedImageUrl) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/download-image?url=${encodeURIComponent(selectedImageUrl)}`);  // Fetch the image from URL
                const blob = await response.blob();  // Convert to Blob
                const fileName = `image_${Date.now()}.jpg`; // Generate a filename
                const file = new File([blob], fileName, { type: blob.type }); // Create a File object

                formData.append('image', file); // Append the file to FormData
            } catch (error) {
                console.error("Error downloading image:", error);
                setLoading(false);
                return;
            }
        } else {
            formData.append('image', selectedImagename || '');
        }

        try {
            const resp = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/updateContent`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setLoading(false);

            if (resp.data.message === 'Post updated successfully') {
                Swal.fire({
                    position: 'center',
                    icon: 'success',
                    title: 'Post updated successfully',
                    showConfirmButton: false,
                    timer: 3000
                });

                setPosts(posts.map(post => post.ContentId === ContentId ?
                    { ...post, Content: updatedContent, image: selectedImageUrl || image } : post
                ));
            } else {
                Swal.fire({
                    position: 'center',
                    icon: 'error',
                    title: 'Error updating post!',
                    showConfirmButton: false,
                    timer: 3000
                });
            }

            setIsUpdating(false);
        } catch (err) {
            console.error('Failed to update post:', err.message);
            setError('Failed to update post');
        }
    };



    return (
        <div>
            {loading ? <div className='spinner'></div> :
                <div className="card shadow-sm p-3" style={{ border: 'none', backgroundColor: '#fff' }}>
                    <div className="card-header" style={{ border: 'none', backgroundColor: '#fff' }}>

                        <h5 className="welcome-text" style={{ fontSize: '20px', color: '#2e3192', textAlign: 'left' }}>
                            Edit Prompt
                        </h5>

                        <hr />

                        <OverlayTrigger placement="bottom" overlay={(props) => (<Tooltip id="tooltip" {...props}>Prompt</Tooltip>)}>

                            <div className='form-group'>
                                <textarea
                                    id='promptTextarea'
                                    value={selectedPrompt}
                                    onChange={handlePromptChange}
                                    className='form-control'
                                    style={{
                                        fontSize: '15px',
                                        borderRadius: '8px',
                                        border: '1px solid #cfeafb',
                                        background: '#FFFFFF',
                                        height: '100px',
                                        width: "100%",
                                        padding: '0px 20px',
                                        fontSize: '16px',
                                    }}
                                />
                            </div>
                        </OverlayTrigger>
                        <button
                            style={{ backgroundColor: '#2e3192', borderRadius: 8 }}
                            onClick={() => handleRegenerate(ContentId, selectedPrompt)}
                            className="bg-blue-500 text-white p-3 rounded-md hover:bg-blue-600 transition-all duration-300 mt-5"
                            disabled={regenerating}
                        >
                            {regenerating ? 'Crafting...' : 'Craft'}
                        </button>
                        <OverlayTrigger placement="bottom" overlay={(props) => (<Tooltip id="tooltip" {...props}>Content</Tooltip>)}>
                            <div className='form-group mt-5'>
                                <textarea
                                    value={updatedContent}
                                    onChange={(e) => {
                                        const content = e.target.value;
                                        if (content.length > 2950) {
                                            Swal.fire({
                                                icon: 'warning',
                                                title: 'Content Length Exceeded',
                                                text: 'The content should not exceed 2950 characters.',
                                                showConfirmButton: true,
                                            });
                                        } else {
                                            setUpdatedContent(content);
                                        }
                                    }}
                                    style={{
                                        fontSize: '15px',
                                        borderRadius: '8px',
                                        border: '1px solid #cfeafb',
                                        background: '#FFFFFF',
                                        height: '800px',
                                        width: "100%",
                                        padding: '0px 20px',
                                        fontSize: '16px',
                                    }}
                                />
                            </div>
                        </OverlayTrigger>

                        {/* AI Media Suggestions Section */}
                        {updatedContent?.trim() && (
                            <div style={{ marginTop: '20px', marginBottom: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                    <h5 style={{ 
                                        color: '#1a202c', 
                                        fontWeight: '600', 
                                        marginBottom: '0',
                                        fontSize: '1.1rem'
                                    }}>
                                        AI Media Suggestions
                                    </h5>
                                    <button
                                        onClick={() => {
                                            setImageKeywords('');
                                            fetchImagesWithKeywords(true);
                                        }}
                                        disabled={isRefreshingImages || isCraftingVisual || !updatedContent?.trim()}
                                        style={{
                                            background: (isRefreshingImages || isCraftingVisual || !updatedContent?.trim()) ? '#cccccc' : '#2e3192',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '6px 12px',
                                            color: 'white',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            cursor: (isRefreshingImages || isCraftingVisual || !updatedContent?.trim()) ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isRefreshingImages && !isCraftingVisual && updatedContent?.trim()) {
                                                e.target.style.backgroundColor = '#1a1d5c';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isRefreshingImages && !isCraftingVisual && updatedContent?.trim()) {
                                                e.target.style.backgroundColor = '#2e3192';
                                            }
                                        }}
                                        title="Refresh images using auto-extracted keywords from your prompt and content"
                                    >
                                        <FontAwesomeIcon 
                                            icon={isRefreshingImages ? faSpinner : faImages} 
                                            style={{ 
                                                fontSize: '12px',
                                                animation: isRefreshingImages ? 'spin 1s linear infinite' : 'none'
                                            }} 
                                        />
                                        {isRefreshingImages ? 'Searching...' : 'Refresh'}
                                    </button>
                                </div>
                                
                                {/* Image Keywords Input */}
                                <div className="mb-3">
                                    <label style={{ 
                                        fontSize: '13px', 
                                        fontWeight: '500', 
                                        color: '#2e3192',
                                        marginBottom: '6px',
                                        display: 'block'
                                    }}>
                                        Image Keywords (Optional - comma-separated)
                                    </label>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <input
                                            type="text"
                                            value={imageKeywords}
                                            onChange={(e) => setImageKeywords(e.target.value)}
                                            placeholder="e.g., business, technology, AI, innovation"
                                            style={{
                                                flex: 1,
                                                fontSize: '14px',
                                                borderRadius: '6px',
                                                border: '1px solid #cfeafb',
                                                background: '#FFFFFF',
                                                padding: '8px 12px',
                                                outline: 'none'
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && imageKeywords.trim()) {
                                                    fetchImagesWithKeywords(false);
                                                }
                                            }}
                                        />
                                        <button
                                            onClick={() => fetchImagesWithKeywords(false)}
                                            disabled={isCraftingVisual || isRefreshingImages || !imageKeywords?.trim()}
                                            style={{
                                                background: (isCraftingVisual || isRefreshingImages || !imageKeywords?.trim()) ? '#cccccc' : '#2e3192',
                                                border: 'none',
                                                borderRadius: '6px',
                                                padding: '8px 16px',
                                                color: 'white',
                                                fontSize: '13px',
                                                fontWeight: '500',
                                                cursor: (isCraftingVisual || isRefreshingImages || !imageKeywords?.trim()) ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                transition: 'all 0.3s ease',
                                                whiteSpace: 'nowrap'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isCraftingVisual && !isRefreshingImages && imageKeywords?.trim()) {
                                                    e.target.style.backgroundColor = '#1a1d5c';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isCraftingVisual && !isRefreshingImages && imageKeywords?.trim()) {
                                                    e.target.style.backgroundColor = '#2e3192';
                                                }
                                            }}
                                            title="Generate images using your keywords"
                                        >
                                            <FontAwesomeIcon 
                                                icon={isCraftingVisual ? faSpinner : faImage} 
                                                style={{ 
                                                    fontSize: '12px',
                                                    animation: isCraftingVisual ? 'spin 1s linear infinite' : 'none'
                                                }} 
                                            />
                                            {isCraftingVisual ? 'Crafting...' : 'Craft Visual'}
                                        </button>
                                    </div>
                                    <p style={{ 
                                        fontSize: '11px', 
                                        color: '#6c757d', 
                                        marginTop: '4px',
                                        marginBottom: '0'
                                    }}>
                                        Enter keywords and click "Craft Visual" to generate images, or leave empty and use "Refresh" for auto-extraction
                                    </p>
                                </div>
                            </div>
                        )}

                        {(isFetchingImages || isRefreshingImages || isCraftingVisual) && (
                            <div className="text-center p-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2 text-muted small">Fetching images...</p>
                            </div>
                        )}

                        <div className="row" style={{ gap: '4px', marginTop: '12px' }}>
                            {fetchedImages.length > 0 ? (
                                fetchedImages.map((img, index) => (
                                    <div key={index} className="col-4 col-md-3 col-lg-2 col-xl-2">
                                        <div 
                                            className="position-relative"
                                            style={{ 
                                                aspectRatio: "1",
                                                borderRadius: "6px",
                                                overflow: "hidden",
                                                cursor: "pointer",
                                                border: selectedImageUrl === img ? "2px solid #2e3192" : "1px solid transparent",
                                                transition: "all 0.2s ease",
                                                backgroundColor: "#f7fafc",
                                                maxWidth: "120px"
                                            }}
                                            onMouseEnter={(e) => {
                                                if (selectedImageUrl !== img) {
                                                    e.currentTarget.style.borderColor = "#cbd5e0";
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (selectedImageUrl !== img) {
                                                    e.currentTarget.style.borderColor = "transparent";
                                                }
                                            }}
                                            onClick={() => handleSelectSuggestedImage(img)}
                                        >
                                            <img
                                                src={img}
                                                alt="Suggested"
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                    display: "block"
                                                }}
                                            />
                                            
                                            {/* Top-right checkbox */}
                                            <div
                className="position-absolute"
                                                style={{ 
                                                    top: "6px", 
                                                    right: "6px",
                                                    width: "22px",
                                                    height: "22px",
                                                    borderRadius: "50%",
                                                    backgroundColor: selectedImagename === img ? "#2e3192" : "rgba(255, 255, 255, 0.95)",
                                                    border: selectedImagename === img ? "none" : "2px solid rgba(0, 0, 0, 0.2)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    cursor: "pointer",
                                                    transition: "all 0.15s ease",
                                                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)"
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const newChecked = !(selectedImagename === img);
                                                    setSelectedImagename(newChecked ? img : null);
                                                    setSelectedImageUrl(newChecked ? img : null);
                                                }}
                                            >
                                                {selectedImagename === img && (
                                                    <FontAwesomeIcon 
                                                        icon={faCheck} 
                                                        style={{ 
                                                            color: "#fff", 
                                                            fontSize: "10px"
                                                        }} 
                                                    />
                                                )}
                                        </div>
                                            
                                            {/* Bottom action buttons overlay */}
                                            <div
                                                className="position-absolute d-flex gap-2"
                                                style={{
                                                    bottom: "8px",
                                                    left: "50%",
                                                    transform: "translateX(-50%)",
                                                    opacity: selectedImageUrl === img ? "1" : "0",
                                                    transition: "opacity 0.2s ease"
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.opacity = "1";
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (selectedImageUrl !== img) {
                                                        e.currentTarget.style.opacity = "0";
                                                    }
                                                }}
                                            >
                                                <button
                                                    className="btn btn-sm"
                                                    style={{
                                                        minWidth: "32px",
                                                        height: "32px",
                                                        padding: "0",
                                                        borderRadius: "6px",
                                                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                                                        border: "none",
                                                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center"
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                    setSelectedImageUrl(img);
                                                    setIsModalOpen(true);
                                                    }}
                                                    title="View Full Image"
                                                >
                                                    <FontAwesomeIcon icon={faImage} style={{ color: "#2e3192", fontSize: "12px" }} />
                                                </button>
                                                
                                                <button
                                                    className="btn btn-sm"
                                                    style={{
                                                        minWidth: "32px",
                                                        height: "32px",
                                                        padding: "0",
                                                        borderRadius: "6px",
                                                        backgroundColor: "#2e3192",
                                                        border: "none",
                                                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center"
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                    setSelectedImagename(img);
                                                    setSelectedImageUrl(img);
                                                    }}
                                                    title="Select This Image"
                                                >
                                                    <FontAwesomeIcon icon={faCheck} style={{ color: "#fff", fontSize: "12px" }} />
                                                </button>
                                            </div>
                                            
                                            {/* Hover overlay */}
                                            {selectedImageUrl !== img && (
                                                <div
                                                    className="position-absolute"
                                                    style={{
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        backgroundColor: "rgba(0, 0, 0, 0)",
                                                        transition: "background-color 0.2s ease",
                                                        pointerEvents: "none"
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.05)";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0)";
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="m-2">No images found for this post</p>
                            )}
                        </div>

                        {/* Modal for Image Preview */}
                        <Modal show={isModalOpen} onHide={() => setIsModalOpen(false)} size={isMaximized ? "xl" : "lg"} centered>
                            <Modal.Header closeButton>
                                <Modal.Title>Image Preview</Modal.Title>
                            </Modal.Header>
                            <Modal.Body className="text-center">
                                <img
                                    src={selectedImageUrl}
                                    alt="Selected"
                                    style={{
                                        maxWidth: "100%",
                                        maxHeight: isMaximized ? "90vh" : "70vh",
                                        objectFit: "contain",
                                    }}
                                />
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
                                    Close
                                </Button>
                                <Button variant="primary" onClick={() => setIsMaximized(!isMaximized)}>
                                    {isMaximized ? "Minimize" : "Maximize"}
                                </Button>
                                <Button variant="success" onClick={() => {
                                    handleSelectSuggestedImage(selectedImageUrl);
                                    setIsModalOpen(false);
                                }}>
                                    Select Image
                                </Button>
                            </Modal.Footer>
                        </Modal>
                        <OverlayTrigger placement="bottom" overlay={(props) => (<Tooltip id="tooltip" {...props}>Upload Image/Video</Tooltip>)}>
                            <div className='form-group mt-3 mb-5'>
                                <input
                                    type='file'
                                    onChange={(e) => {
                                        handleImageChange(e); // Keep existing upload handling
                                        setSelectedImageUrl(null); // Clear suggested image selection when a file is chosen
                                    }}
                                    className='form-control'
                                    accept='image/*,video/*'
                                />
                                {selectedImagename && <p>{selectedImagename}</p>}
                            </div>
                        </OverlayTrigger>

                        <div className="btn-group mt-3">
                            <button onClick={handleSaveUpdate} className="btn btn-primary ml-2">Update</button>
                        </div>
                    </div>
                </div>
            }
        </div>
    );
};

export default PendingPost;
